/**
 * Indigo / Inter -- the design system's single source of colour truth.
 *
 * `lightTokens` / `darkTokens` are the raw design tokens, spelled exactly as
 * the spec names them. Everything else in the app reaches them one of three
 * ways:
 *
 *   1. `theme.palette.*` (MUI slots), for `sx` callbacks and styled components.
 *   2. `theme.palette.custom.*`, for the tokens MUI has no slot for
 *      (sidebar, tableHeader, rowHover, ...).
 *   3. `var(--dc-*)` CSS custom properties, emitted onto `:root` by the
 *      CssBaseline override in `theme/index.js`. These are what the many
 *      plain-string `sx` values (`border: '1px solid var(--dc-border)'`) use,
 *      since those have no `theme` in scope and previously hard-coded a hex
 *      that could not follow the theme mode.
 *
 * Keep the three in sync by editing the token objects below -- never the
 * derived palettes by hand.
 */

export const lightTokens = {
  primary: '#5664B8',
  // `primary` is both a fill and a text colour, and the two have opposite
  // contrast needs. As a fill it is fine -- white on #5664B8 is 5.37:1. As
  // text it has to clear 4.5:1 against the surface instead, which it does in
  // light mode (5.37:1 on card) but badly fails in dark (2.81:1). So links,
  // selected tabs and any other indigo *text* read this token, and the fill
  // keeps `primary` untouched in both modes.
  primaryText: '#5664B8',
  primaryHover: '#4B58A5',
  primaryActive: '#424E94',
  primarySoft: '#EEF0FB',
  primarySubtle: '#F4F5FD',
  // The scrollbar thumb. It is chrome, not content, so it sits well below the
  // text ramp -- but far enough above the page and card surfaces to be found
  // without scrolling first. The sidebar hover fill this started from measured
  // 1.02:1 on the page, i.e. invisible.
  scrollbar: '#D2D2DA',
  background: '#F6F7FC',
  // The sidebar reads as the same surface family as cards, a hair below them.
  // The page `background` behind it is the tinted one, so the rail still
  // separates from the content area -- the `border` hairline on its right edge
  // carries the rest.
  sidebar: '#FBFBFE',
  /**
   * A neutral off-white rather than #FFFFFF. A full-white card is the brightest
   * thing a screen can emit, and this app tiles them: deal tables, stakeholder
   * lists, highlight cards, integration tiles. This sits ~6% below white in
   * luminance, with the blue cast taken out (chroma 1, against 3 for the cool
   * near-white it replaced) so the surfaces read as grey and the indigo is left
   * to do its work in the accents.
   *
   * The surfaces underneath it kept their original cool tint, so the steps
   * between them are narrow: card sits 1.036:1 above the page background,
   * 1.028:1 above `rowHover` and 1.084:1 above `tableHeader`. That is about
   * half the separation either the all-white or the all-neutral palette had,
   * and it is the axis to watch -- a card dissolves into the page from this
   * direction long before anything fails a contrast check.
   *
   * `selectedRow` and the `primary*` tints deliberately keep their indigo: a
   * selected row has to read as selected against neutral surroundings.
   */
  card: '#fff',
  surfaceRaised: '#FFFFFF',
  border: '#E1E4F1',
  borderStrong: '#CDD3E3',
  divider: '#E8EAF2',
  heading: '#273047',
  text: '#4F5A70',
  // The light text ramp is darker than the spec drew it, because AA and a
  // 3:1 "muted" grey cannot both hold. Measured against the three surfaces
  // these actually render on (card / background / tableHeader):
  //   textSecondary was #697394 -- 4.69 / 4.38 / 4.19, failing on two.
  //   textMuted     was #8B94AA -- 3.04 / 2.84 / 2.71, failing on all three.
  // `textSecondary` is the one that mattered most: MuiTableCell.head paints it
  // on `tableHeader`, so every table column header sat at 4.19:1.
  textSecondary: '#5E6880',
  // Re-darkened twice as the surfaces above came down -- #676F7D, then #646C7A
  // -- because each step costs the text ramp contrast. With `tableHeader` and
  // `rowHover` back on their original tint it now measures 4.86:1 on the worst
  // of the four surfaces it renders on, `tableHeader`.
  textMuted: '#636A78',
  // Unchanged: disabled text is exempt under WCAG 1.4.3.
  textDisabled: '#AEB5C5',
  tableHeader: '#F0F2F8',
  rowHover: '#F7F8FC',
  selectedRow: '#EEF0FB',
  inputBackground: '#F8F8F9',
  // Status colours are used as text and icon fills in this app, never as a
  // background, so they are tuned for contrast against the surface. The
  // previous values (#5E8B76 / #B58A55 / #B36B73) sat at 3.87 / 3.12 / 3.99
  // on card -- all below AA for body text.
  success: '#41705A',
  warning: '#8A6224',
  danger: '#97474F',
  /**
   * The brand mark is a single charcoal (#343A40) artwork with no light-mode
   * variant to swap to, so dark mode re-tones it in CSS instead of shipping a
   * second asset. `hue-rotate(180deg)` after the invert is what keeps it
   * neutral: inverting a blue-grey alone lands on a warm beige (#CBC5BF),
   * where the pair lands on #C1C7CD -- 10.2:1 on the dark sidebar, and in the
   * same cool family as the dark text ramp above.
   *
   * This is for the DealCopilot mark only. Third-party logos (CRM, meeting
   * providers) and customer artwork must never take it -- inverting someone
   * else's brand colour is worse than leaving it dark.
   */
  logoFilter: 'none',
  /**
   * The `/dh-icons` artwork (activity types, integration glyphs) is flat
   * charcoal line art with no light-mode variant either, and it disappears
   * against a dark surface. Unlike `logoFilter` this one is composable - the
   * light value is the no-op `invert(0)` rather than `none` - so a call site
   * that already has a filter can append it:
   * `filter: 'grayscale(100%) var(--dc-mono-icon-filter)'`.
   *
   * Monochrome artwork only. A third-party logo that carries brand colour
   * must not take it.
   */
  monoIconFilter: 'invert(0)',
};

export const darkTokens = {
  // The spec keeps the Indigo accent unchanged in dark mode -- only surfaces,
  // borders and text contrast move.
  primary: '#5664B8',
  // The fill above stays on-brand; indigo *text* has to lift off the dark
  // surface to be readable. #5664B8 scored 2.81:1 on the #222631 card, which
  // made every link and selected tab in dark mode near-invisible. This is
  // 6.29:1 on the same surface.
  primaryText: '#99A3E6',
  // Dark mode lifts on hover rather than deepening -- a darker indigo would
  // sink into the #222631 card instead of reading as a response.
  primaryHover: '#6F7BD0',
  primaryActive: '#8089D9',
  primarySoft: '#30364A',
  primarySubtle: '#272C3A',
  // Dark mode lifts rather than darkens, so the thumb sits a step above the
  // page the way the light one sits a step below it.
  scrollbar: '#3A4052',
  background: '#171A22',
  sidebar: '#1D202A',
  card: '#222631',
  surfaceRaised: '#2A2F3C',
  border: '#343A49',
  borderStrong: '#434B5E',
  divider: '#2C3140',
  heading: '#EDF0F6',
  text: '#D7DCE7',
  textSecondary: '#C7CEDD',
  textMuted: '#9CA7BB',
  // Lifted from #7C8699, which sat at 4.12:1 on the card.
  textDisabled: '#8A93A6',
  tableHeader: '#20242E',
  rowHover: '#292E3B',
  selectedRow: '#30364A',
  inputBackground: '#1B1E27',
  success: '#7FAE97',
  warning: '#D0A876',
  danger: '#D18C93',
  logoFilter: 'invert(1) hue-rotate(180deg)',
  monoIconFilter: 'invert(1) hue-rotate(180deg)',
};

export const light = {
  mode: 'light',
  alternate: {
    main: lightTokens.background,
    dark: lightTokens.primarySoft,
  },
  cardShadow: 'rgba(39, 48, 71, 0.06)',
  primary: {
    main: lightTokens.primary,
    // Mid-tint rather than the pale `primarySoft`: `primary.light` is painted
    // as a 2px border in the top nav, where a near-white would vanish.
    light: '#8C97D4',
    dark: lightTokens.primaryHover,
    contrastText: '#FFFFFF',
  },
  secondary: {
    light: '#D6B489',
    main: lightTokens.warning,
    dark: '#96713F',
    contrastText: '#FFFFFF',
  },
  text: {
    primary: lightTokens.text,
    secondary: lightTokens.textSecondary,
    disabled: lightTokens.textDisabled,
    // `blue` is indigo used as text, so it reads the text token, not the fill.
    blue: lightTokens.primaryText,
    muted: lightTokens.textMuted,
  },
  // The spec keeps `--divider` a shade lighter than `--border`: borders box a
  // surface in, dividers only separate content inside one.
  divider: lightTokens.divider,
  background: {
    paper: lightTokens.card,
    default: lightTokens.background,
    // `level1` is the app's long-standing "hairline" colour -- it is read both
    // as a border and as a quiet fill, so it maps to the border token.
    level1: lightTokens.border,
    level2: lightTokens.tableHeader,
  },
  error: {
    main: lightTokens.danger,
    dark: '#7D3A41',
    light: '#C0868C',
    contrastText: '#FFFFFF',
  },
  success: {
    main: lightTokens.success,
    dark: '#325847',
    light: '#7FA893',
    contrastText: '#FFFFFF',
  },
  info: {
    main: lightTokens.primary,
    dark: lightTokens.primaryHover,
    light: '#8C97D4',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: lightTokens.warning,
    dark: '#6E4E1A',
    light: '#BE9455',
    contrastText: '#FFFFFF',
  },
  custom: { ...lightTokens },
};

export const dark = {
  mode: 'dark',
  alternate: {
    main: darkTokens.sidebar,
    dark: darkTokens.background,
  },
  cardShadow: 'rgba(0, 0, 0, 0.28)',
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: {
    main: darkTokens.primary,
    light: darkTokens.primaryActive,
    // `primary.dark` is what MUI paints on a contained button's hover. In dark
    // mode the response is a lift, not a deepening, so it points at the
    // lighter hover token rather than a darker indigo.
    dark: darkTokens.primaryHover,
    contrastText: '#FFFFFF',
  },
  secondary: {
    light: '#E2C4A0',
    main: darkTokens.warning,
    dark: '#A8845A',
    contrastText: '#171A22',
  },
  text: {
    primary: darkTokens.text,
    secondary: darkTokens.textSecondary,
    disabled: darkTokens.textDisabled,
    // See the light-mode note: indigo as text, so it reads `primaryText`.
    blue: darkTokens.primaryText,
    muted: darkTokens.textMuted,
  },
  divider: darkTokens.divider,
  background: {
    paper: darkTokens.card,
    default: darkTokens.background,
    level1: darkTokens.border,
    level2: darkTokens.tableHeader,
  },
  error: {
    main: darkTokens.danger,
    dark: '#AE6E75',
    light: '#E0A9AF',
    contrastText: '#171A22',
  },
  success: {
    main: darkTokens.success,
    dark: '#628E79',
    light: '#A0C6B2',
    contrastText: '#171A22',
  },
  info: {
    main: darkTokens.primary,
    dark: darkTokens.primaryHover,
    light: darkTokens.primaryActive,
    contrastText: '#FFFFFF',
  },
  warning: {
    main: darkTokens.warning,
    dark: '#A8845A',
    light: '#E2C4A0',
    contrastText: '#171A22',
  },
  custom: { ...darkTokens },
};
